import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { MyHrMassRoleDialog } from "my-hr/components";
import { MY_HR_GET_MODULE_ROLES, MY_HR_REMOVE_MASS_ROLE } from "my-hr/graphql";
import { OcConfirmationDialog } from "oc/components";
import { useOcClientState, useOcPolling } from "oc/hooks";
import getModuleIcon from "oc/utils/get-modul-icon";

// Types for role filters and GraphQL
interface RoleFilters {
  company: string[];
  orgUnit: string[];
  workingPlace: string[];
  jobClassGroup: string[];
  payOffice: string[];
  contract: string[];
}

interface ModuleRole {
  module: string;
  roles: string[];
}

interface GetModuleRolesData {
  moduleRoles: ModuleRole[];
}

interface RemoveMassRoleVars {
  roleFilters: RoleFilters;
  roleFiltering: unknown;
  module?: string;
  role: string;
}

interface RemoveMassRoleResult {
  removeMassRole: string;
}

// Props for component
interface MyHrRemoveMassRoleProps {
  roleFilters: RoleFilters;
  roleFiltering: unknown;
  isMobile: boolean;
}

// Styled select for module picker
const StyledSelect = styled(Select) <SelectProps<string>>`
  .MuiOutlinedInput-input {
    padding: 16px 14px;
    background-color: white;
  }
`;

type Result = {
  errors?: any[];
  processed?: number;
  added?: number;
};

export const MyHrRemoveMassRole: React.FC<MyHrRemoveMassRoleProps> = ({
  roleFilters,
  roleFiltering,
  isMobile,
}) => {
  const { t } = useTranslation();

  // Persistent state
  const [selectedProduct, setSelectedProduct] = useOcClientState<string>(
    "removeMassRoleModule",
    "MyHR",
  );
  const [selectedRole, setSelectedRole] = useOcClientState<string>(
    "removeMassRoleRole",
    "",
  );

  const [result, setResult] = useState<Result | null>(null);

  // Local UI state
  const [showRoleFiltering, setShowRoleFiltering] =
    React.useState<boolean>(false);
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState<boolean>(false);

  // Fetch modules and roles
  const { data: productsData } = useQuery<GetModuleRolesData>(
    MY_HR_GET_MODULE_ROLES,
    {
      fetchPolicy: "cache-and-network",
      pollInterval: useOcPolling(60000),
    },
  );

  const selectedModule = productsData?.moduleRoles.find(
    (m) => m.module === selectedProduct,
  );
  const availableRoles = selectedModule?.roles ?? [];

  // GraphQL mutation
  const [removeMassRole] = useMutation<
    RemoveMassRoleResult,
    RemoveMassRoleVars
  >(MY_HR_REMOVE_MASS_ROLE, {
    onCompleted: ({ removeMassRole }) => {
      // Try to parse the string as JSON, fallback to default Result if parsing fails
      try {
        const parsed: Result =
          typeof removeMassRole === "string"
            ? JSON.parse(removeMassRole)
            : removeMassRole;
        setResult(parsed);
      } catch {
        setResult({
          errors: [{ message: removeMassRole || "" }],
          processed: 0,
          added: 0,
        });
      }
    },
    onError: (err) => console.error(err),
  });

  // Handlers
  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
      setSelectedProduct(event.target.value as string);
    },
    [setSelectedProduct],
  );

  const handleCancel = useCallback(() => {
    setSelectedProduct("MyHR");
    setSelectedRole("");
    setResult(null);
  }, [setSelectedProduct, setSelectedRole, setResult]);

  const handleShowDialog = useCallback(() => {
    setShowDialog((prev) => !prev);
  }, []);

  const handleFinish = useCallback(() => {
    setShowDialog(false);
    handleCancel();
  }, [handleCancel]);

  const handleRemoveRole = useCallback(() => {
    const vars: RemoveMassRoleVars = {
      roleFilters,
      roleFiltering,
      module: selectedModule?.module,
      role: selectedRole,
    };
    setShowDialog(true);
    setShowRoleFiltering(false);
    removeMassRole({ variables: vars });
  }, [
    roleFilters,
    roleFiltering,
    selectedModule,
    selectedRole,
    removeMassRole,
  ]);

  const handleShowConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog(true);
  }, []);

  const handleOKConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog(false);
    handleRemoveRole();
  }, [handleRemoveRole]);

  const handleCancelConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog(false);
  }, []);

  const generateConfirmationText = useCallback((): string => {
    const empty = Object.values(roleFilters).every((arr) => arr.length === 0);
    return empty
      ? "MY_HR_REMOVE_GROUP_ROLE_TO_ALL_DIALOG_TEXT"
      : "MY_HR_REMOVE_GROUP_ROLE_DIALOG_TEXT";
  }, [roleFilters]);

  // Reset on unmount
  useEffect(() => {
    return () => {
      handleCancel();
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // Reset role when product changes
  useEffect(() => {
    setSelectedRole("");
  }, [selectedProduct]); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmationDialog}
        title={t("MY_HR_REMOVE_GROUP_ROLE_DIALOG_TITLE")}
        texts={[t(generateConfirmationText())]}
        okButtonText={t("OC_SAVE")}
        cancelButtonText={t("OC_CANCEL")}
        onClose={handleOKConfirmationDialog}
        onCancel={handleCancelConfirmationDialog}
      />
      <MyHrMassRoleDialog
        onShowDialog={handleShowDialog}
        showDialog={showDialog}
        onFinish={handleFinish}
        onCloseDialog={() => setShowDialog(false)}
        result={result}
        infoText={t("MY_HR_REMOVE_MASS_ROLE_INFO")}
        successText={t("MY_HR_REMOVE_ROLE_SUCCEEDED")}
        successInfo={t("MY_HR_ROLE_REMOVED")}
        toolbarLabel={t("MY_HR_REMOVE_MASS_ROLE")}
        errorText={t("MY_HR_REMOVE_ROLE_ERROR")}
        action="remove"
        isMobile={isMobile}
      />
      <Collapse in={!showRoleFiltering} timeout="auto" unmountOnExit>
        <Box display="flex" flexDirection="column" alignItems="center" pt={3}>
          <Card
            variant="outlined"
            style={{
              width: isMobile ? "100%" : 696,
              borderRadius: 1,
              borderColor: "white",
              overflow: "visible",
              marginBottom: "16px",
              backgroundColor: "#F1F3F4",
            }}
          >
            <Box p={2} display="flex" flexDirection="column">
              <Box
                fontSize="20px"
                letterSpacing="0.15px"
                padding="15px 0px"
                paddingBottom="30px"
              >
                {t("MY_HR_REMOVE_ROLE")}
              </Box>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                paddingBottom="8px"
              >
                <Box width={isMobile ? "100%" : "50%"} paddingRight="8px">
                  <FormControl
                    fullWidth
                    variant="outlined"
                    style={{ backgroundColor: "white" }}
                  >
                    <InputLabel id="module-select-label">
                      {t("OC_MODULE")}
                    </InputLabel>
                    <StyledSelect
                      labelId="module-select-label"
                      id="remove-mass-role-input"
                      value={selectedProduct}
                      onChange={handleChange}
                      label={t("OC_MODULE")}
                    >
                      {productsData?.moduleRoles.map((module) => (
                        <MenuItem key={module.module} value={module.module}>
                          <Box display="flex" alignItems="center">
                            {getModuleIcon(module.module)}
                            <Typography
                              variant="body1"
                              style={{
                                marginLeft: "22px",
                              }}
                            >
                              {module.module}
                            </Typography>

                          </Box>
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </Box>
                {isMobile && <Box height="16px" />}
                <Box
                  width={isMobile ? "100%" : "50%"}
                  paddingLeft={isMobile ? "0px" : "8px"}
                >
                  <ButtonGroup
                    orientation="vertical"
                    fullWidth
                    variant="contained"
                    disableElevation
                  >
                    {availableRoles.map((role) => (
                      <Button
                        key={role}
                        id={role}
                        onClick={() => setSelectedRole(role)}
                        disableElevation
                        color="secondary"
                        variant="text"
                        disabled={
                          role === "admin" ||
                          (selectedModule?.module === "MyHR" && role === "user")
                        }
                        style={
                          role === selectedRole
                            ? {
                              backgroundColor: "#cc0033",
                              color: "white",
                            }
                            : {
                              backgroundColor: "white",
                              color: role === "admin" ||
                                (selectedModule?.module === "MyHR" && role === "user")
                                ? "#C6CACC" : "#212121",
                            }
                        }
                      >
                        {t(`MY_HR_ROLE_${role.toUpperCase()}`)}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection={isMobile ? "column-reverse" : "row"}
                justifyContent="flex-end"
                padding={isMobile ? "0px" : "10px 0px 0px 0px"}
              >
                <Button
                  id="cancel-remove-mass-role"
                  onClick={handleCancel}
                  color="secondary"
                  variant="text"
                >
                  {t("OC_CANCEL")}
                </Button>
                {isMobile && <Box height="16px" />}
                <Button
                  id="confirm-remove-mass-role"
                  variant="contained"
                  color="primary"
                  disableElevation
                  style={{ marginLeft: isMobile ? "0px" : "16px" }}
                  disabled={!selectedRole}
                  onClick={handleShowConfirmationDialog}
                >
                  {t("MY_HR_REMOVE")}
                </Button>
                {isMobile && <Box height="16px" />}
              </Box>
            </Box>
          </Card>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MyHrRemoveMassRole;
